import { useState, type Dispatch, type SetStateAction } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as apiClient from 'api/apiClients';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import FormInput from 'components/forms/FormInput';
import ClientShareLocation from './ClientShareLocation';

interface ClientInfoFormProps {
    client: any;
    fetchClient: () => Promise<void>;
    setClient: Dispatch<SetStateAction<any>>;
}

const ClientInfoForm = ({ client, fetchClient, setClient }: ClientInfoFormProps) => {
    const { t } = useTranslation(['Clients', 'common']);
    const { auth } = useAuth();
    const { showToast } = useToast();
    const [showChangeLocation, setShowChangeLocation] = useState(false);
    const [showChangePhone, setShowChangePhone] = useState(false);

    const handleChangeLocation = async (newLocation: string) => {
        const resSetLocation = await apiClient.setClientField(client.ID, 'client_location', newLocation);
        if (!resSetLocation) return showToast(t('common:messError'), 'danger');
        setClient((prev: any) => ({ ...prev, clientLocation: newLocation }));
        await fetchClient();
        showToast(t('msssLocationChanged'), 'success');
    };

    const handleChangePhone = async (newPhone: string) => {
        const resSetPhone = await apiClient.setClientField(client.ID, 'client_phone', newPhone);
        if (!resSetPhone) return showToast(t('common:messError'), 'danger');
        setClient((prev: any) => ({ ...prev, clientPhone: newPhone }));
        await fetchClient();
        showToast(t('msssPhoneChanged'), 'success');
    };

    return (
        <ul className="list-group list-group-flush">
            <li className="list-group-item px-0 pt-0">
                <p className="mb-1 text-muted fw-medium">{t('labelName')}</p>
                <h5 className="mb-0 ">{client?.clientName || '-'}</h5>
            </li>
            <li className="list-group-item px-0 py-2">
                <div className="d-flex justify-content-between">
                    <div className="text-truncate w-100 d-block">
                        <p className="mb-1 text-muted fw-medium">{t('labelPhone')}</p>
                        <p className="mb-0 text-truncate w-100 d-block">{client?.clientPhone || '-'}</p>
                    </div>
                    <div className="text-end" style={{ width: 200 }}>
                        {auth?.user?.access?.role === 'manager' && (
                            <Button
                                className="px-2 d-inline-block me-1"
                                size="sm"
                                variant="light-secondary"
                                title={t('labelChangePhone')}
                                onClick={(e: any) => {
                                    e.target.blur();
                                    setShowChangePhone(true);
                                }}
                            >
                                <i className="bi bi-pencil-square fs-14"></i>
                            </Button>
                        )}

                        <a className="btn btn-sm btn-primary text-white px-2" href={`tel:${client?.clientPhone || '-'}`}>
                            <i className="bi bi-telephone-outbound fs-14"></i>
                        </a>
                    </div>
                </div>
                {showChangePhone && (
                    <div>
                        <FormInput
                            showForm={showChangePhone}
                            title={t('labelChangePhone')}
                            setShowForm={setShowChangePhone}
                            defaultValue={client?.clientPhone}
                            type="text"
                            as="input"
                            callBack={handleChangePhone}
                        />
                    </div>
                )}
            </li>

            {client?.clientEmail && (
                <li className="list-group-item px-0 py-2">
                    <div className="d-flex justify-content-between">
                        <div className="text-truncate w-100 d-block">
                            <p className="mb-1 text-muted fw-medium">{t('labelEmail')}</p>
                            <p className="mb-0 text-truncate w-100 d-block">{client?.clientEmail || '-'}</p>
                        </div>
                        <div className="text-end">
                            <a className="btn btn-sm btn-warning text-white px-2" href={`mailto:${client?.clientEmail || '-'}`}>
                                <i className="bi bi-envelope fs-14"></i>
                            </a>
                        </div>
                    </div>
                </li>
            )}

            {client?.clientAddress && (
                <li className="list-group-item px-0 pb-0 py-2 ">
                    <p className="mb-1 text-muted fw-medium">{t('labelAddress')}</p>
                    <p className="mb-0">{client?.clientAddress || '-'}</p>
                </li>
            )}

            <li className="list-group-item px-0 py-2  mb-3">
                <div className="d-flex justify-content-between">
                    <div className="text-truncate w-100 d-block">
                        <p className="mb-1 text-muted fw-medium">{t('labelLocation')}</p>
                        <p className="mb-0 text-truncate w-100 d-block">
                            <a target="_blank" rel="noreferrer" href={client?.clientLocation || '-'}>
                                {client?.clientLocation || '-'}
                            </a>
                        </p>
                    </div>
                    <div className="text-end" style={{ width: 200 }}>
                        <Button
                            className="px-2 d-inline-block me-1"
                            size="sm"
                            variant="light-secondary"
                            title={t('labelChangeLocation')}
                            onClick={(e: any) => {
                                e.target.blur();
                                setShowChangeLocation(true);
                            }}
                        >
                            <i className="bi bi-pencil-square fs-14"></i>
                        </Button>

                        <Button
                            className="px-2 d-inline-block me-1"
                            size="sm"
                            variant="primary"
                            title={t('labelPasteLocation')}
                            onClick={async (e: any) => {
                                e.target.blur();
                                const newLocation = await navigator.clipboard.readText();
                                handleChangeLocation(newLocation);
                            }}
                        >
                            <i className="bi bi-clipboard-check fs-14"></i>
                        </Button>

                        <div className="d-inline-block">
                            <ClientShareLocation client={client} locationUrl={client?.clientLocation} />
                        </div>
                    </div>
                </div>

                {showChangeLocation && (
                    <div>
                        <FormInput
                            showForm={showChangeLocation}
                            title={t('labelSetLocation')}
                            setShowForm={setShowChangeLocation}
                            defaultValue={client?.clientLocation}
                            type="text"
                            as="input"
                            callBack={handleChangeLocation}
                        />
                    </div>
                )}
            </li>
        </ul>
    );
};

export default ClientInfoForm;
